import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { selectState, actionProps } from 'utils/reduxActions';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialIcon from '@material-ui/lab/SpeedDialIcon';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PostAddIcon from '@material-ui/icons/PostAdd';
import EcoIcon from '@material-ui/icons/Eco';
import Filters from './components/Filters';
import Stats from './components/Stats';
import ItemList from './components/ItemList';
import { scheduleManagementActions } from './reducer';
import { suppliersActions } from 'screens/Suppliers/reducer';
import { varietiesActions } from 'screens/Varieties/reducer';
import { clone, falsy, validateRut, validatePhone } from 'utils/functions';
import saga from './saga';
import suppliersSaga from 'screens/Suppliers/saga';
import varietiesSaga from 'screens/Varieties/saga';
import connectSaga from 'utils/saga';
import SupplierForm from 'screens/Suppliers/components/SupplierForm';
import VarietyForm from 'screens/Varieties/components/VarietyForm';
import BookingForm from './components/BookingForm';
import CompleteModal from './components/CompleteModal';
import DeleteModal from 'commons/components/DeleteModal';
import TableActionMenu from 'commons/components/TableActionMenu';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    minHeight: '100vh',
    position: 'relative',
  },
  title: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(3),
    color: '#455a64',
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    '& .MuiSpeedDial-fab': {
      backgroundColor: '#008b76',
      '&:hover': {
        backgroundColor: '#007a68',
      }
    },
    '& .MuiSpeedDialAction-staticTooltipLabel': {
      width: '140px',
      textAlign: 'center',
    }
  },
  marginTop: {
      marginTop: theme.spacing(2)
  }
}));

function ScheduleManagement(props) {
  let schedule_management_read = Object.values(props.objects.roles)[0].name === 'schedule_management';
  let schedule_management_edit = Object.values(props.objects.roles)[0].name === 'schedule_management_edit';
  
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    objects,
    actions,
    formControls,
    invalidControls,
    formIsOpen,
    edit,
    bookings,
    filters,
    bookingsSummary,
    bookingFormIsOpen,
    bookingFormControls,
    bookingInvalidControls,
    suppliersList,
    varietiesList,
    operationTypes,
    truckTypes,
    loadConditions,
    varietyFormIsOpen,
    varietyFormControls,
    varietyInvalidControls,
    varietyEdit,
    varietyActions,
  } = props;
  
  const permissions = useSelector((state) => state.app.permissions) || [];
  const hasSchedulePermission = permissions.includes('schedule_management');
  
  const [openFab, setOpenFab] = useState(false);


  React.useEffect(() => {
    actions.getStateFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFabClose = (event, reason) => {
    if (reason === 'toggle' || reason === 'blur' || reason === 'escapeKeyDown') {
      setOpenFab(false);
    }
  };

  const handleFabOpen = (event, reason) => {
    if (reason === 'toggle') {
      setOpenFab(true);
    }
  };

  // Supplier Form Handlers
  const toggleSupplierForm = () => {
    actions.toggleForm('formIsOpen');
  };

  const toggleVarietyForm = () => {
    varietyActions.toggleForm('formIsOpen');
  };

  const toggleBookingForm = () => {
    props.dispatch(scheduleManagementActions.toggleScheduleForm('bookingFormIsOpen'));
  };

  const changeControls = (event) => {
    actions.changeControls({ [event.target.id]: event.target.value }, { [event.target.id]: false });
  };

  const submitSupplier = () => {
    const account = Object.values(objects.accounts)[0] || {};
    const invalidPhone = hasSchedulePermission && !validatePhone(formControls.phone);
    if (
      falsy(formControls.name) ||
      falsy(formControls.taxNumber) ||
      validateRut(formControls.taxNumber, account?.country) ||
      invalidPhone
    ) {
      actions.setInvalidControls({
        name: falsy(formControls.name),
        taxNumber: falsy(formControls.taxNumber)
          ? true
          : validateRut(formControls.taxNumber, account?.country),
        phone: invalidPhone,
      });
    } else {
      actions.createSupplier(formControls);
    }
  };

  const submitBooking = () => {
    const isPickup = bookingFormControls.operationType === 'PICKUP';
    const isDispatch = bookingFormControls.operationType === 'DISPATCH';
    
    // Always required (deshabilitados preventivamente si es 'edit')
    const invalidSupplierId = !props.edit && falsy(bookingFormControls.supplierId);
    const invalidOperationType = !props.edit && falsy(bookingFormControls.operationType);
    const invalidTruckType = !props.edit && falsy(bookingFormControls.truckType);
    const invalidLoadConditions = !props.edit && falsy(bookingFormControls.loadConditions);

    // Conditionally required for Pickups (deshabilitados preventivamente si es 'edit')
    const invalidVarietyId = !props.edit && isPickup && falsy(bookingFormControls.varietyId);
    const invalidDate = !props.edit && falsy(bookingFormControls.date); // Date is always required
    const invalidArrivalFieldAt = !props.edit && isPickup && falsy(bookingFormControls.arrivalFieldAt);
    const invalidArrivalProcessingPlant = !props.edit && (isPickup || isDispatch) && falsy(bookingFormControls.arrivalProcessingPlant);
    const invalidProcessingSiteId = !props.edit && isPickup && falsy(bookingFormControls.processingSiteId);
    
    // Carrier conditionally required (deshabilitados preventivamente si es 'edit')
    const invalidCarrierId = !props.edit && bookingFormControls.needCarrier && falsy(bookingFormControls.carrierId);

    if (
        invalidSupplierId ||
        invalidOperationType ||
        invalidTruckType ||
        invalidLoadConditions ||
        invalidVarietyId ||
        invalidDate ||
        invalidArrivalFieldAt ||
        invalidArrivalProcessingPlant ||
        invalidProcessingSiteId ||
        invalidCarrierId
    ) {
         actions.setScheduleInvalidControls({
            supplierId: invalidSupplierId,
            operationType: invalidOperationType,
            truckType: invalidTruckType,
            loadConditions: invalidLoadConditions,
            varietyId: invalidVarietyId,
            date: invalidDate,
            arrivalFieldAt: invalidArrivalFieldAt,
            arrivalProcessingPlant: invalidArrivalProcessingPlant,
            processingSiteId: invalidProcessingSiteId,
            carrierId: invalidCarrierId,
         });
    } else {
        if (props.edit) {
             actions.updateBooking(props.bookingIdToEdit, bookingFormControls);
        } else {
             actions.createReceipt(bookingFormControls);
        }
    }
  };

  const fabActions = [
    { icon: <PostAddIcon />, 
      name: t('scheduleManagement.form.addTitle'), 
      onClick: toggleBookingForm,
    },
    /* {
      icon: <EcoIcon />,
      name: t('scheduleManagement.form.createVariety'),
      onClick: toggleVarietyForm,
    }, */
    {
      icon: <PersonAddIcon />,
      name: t('scheduleManagement.form.createSupplier'),
      onClick: toggleSupplierForm,
    },
  ];

  return (
    <Container maxWidth={false} className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h1" className={classes.title}>
            {t('Schedule Management')}
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Filters objects={objects} actions={actions} filters={filters} loadConditions={loadConditions} />
        </Grid>

        <Grid container item>
          <Grid item xs={12} sm={4} md={3}>
            <TableActionMenu
              menuItems={[
                {
                  label: t('Export'),
                  listItems: [
                    {
                      label: t('Excel File'),
                      onClick: actions.exportBookings,
                      params: [],
                    },
                  ],
                },
              ]}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Stats summary={bookingsSummary} />
        </Grid>

        <Grid item xs={12}>
          <ItemList />
        </Grid>
      </Grid>

      <BookingForm
        isOpen={bookingFormIsOpen}
        toggleForm={toggleBookingForm}
        changeControls={(event) => {
          if (event.target) {
            actions.changeScheduleControls({ [event.target.id]: event.target.value }, { [event.target.id]: false });
          } else {
            actions.changeScheduleControls(event, {});
          }
        }}
        submit={submitBooking}
        controls={bookingFormControls}
        invalidControls={bookingInvalidControls}
        suppliers={suppliersList}
        edit={props.edit}
        operationTypes={operationTypes}
        truckTypes={truckTypes}
        loadConditions={loadConditions}
        complete={() => actions.toggleCompleteModal(props.bookingIdToEdit)}
        objects={objects}
      />

      <CompleteModal
        isOpen={props.completeIsOpen}
        toggleForm={actions.toggleCompleteModal}
        submit={(arrivalAt) => actions.completeBooking(props.bookingIdToEdit, arrivalAt)}
      />

      <DeleteModal
        isOpen={props.deleteIsOpen}
        toggleForm={actions.toggleDelete}
        submit={() => actions.deleteReceipt(props.toDeleteId)}
        buttonId="delete-booking"
      />
      
      <SupplierForm
        isOpen={formIsOpen}
        toggleForm={toggleSupplierForm}
        changeControls={changeControls}
        onChange={actions.changeControls}
        controls={formControls}
        edit={edit}
        submit={submitSupplier}
        invalidControls={invalidControls}
        country={Object.values(objects.accounts)[0]?.country}
        hasSchedulePermission={hasSchedulePermission}
      />

      <VarietyForm
        isOpen={varietyFormIsOpen}
        toggleForm={() => varietyActions.toggleForm('formIsOpen')}
        changeControls={(event) => {
            if (event.autocomplete) {
                varietyActions.changeControls({ [event.input]: event.value }, { [event.input]: false });
              } else {
                varietyActions.changeControls(
                  { [event.currentTarget.id]: event.target.value },
                  { [event.currentTarget.id]: false },
                );
              }
        }}
        controls={varietyFormControls}
        fruits={objects.fruits}
        edit={varietyEdit}
        submit={() => varietyActions.createVariety(varietyFormControls)}
        invalidControls={varietyInvalidControls}
      />

      {(!schedule_management_read && !schedule_management_edit) && (
      <SpeedDial
        ariaLabel="SpeedDial"
        className={classes.fab}
        icon={<SpeedDialIcon />}
        onClose={handleFabClose}
        onOpen={handleFabOpen}
        open={openFab}
        direction="up"
      >
        {fabActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            tooltipOpen
            onClick={action.onClick}
            FabProps={{ disabled: action.disabled }}
          />
        ))}
      </SpeedDial>
      )}
    </Container>
  );
}

const makeMapStateToProps = selectState(
  'app.objects',
  'suppliers.formControls',
  'suppliers.invalidControls',
  'suppliers.formIsOpen',
  'suppliers.edit',
  'suppliers.toEditId',
  'scheduleManagement.bookings',
  'scheduleManagement.filters',
  'scheduleManagement.bookingsSummary',
);

const mapStateToProps = (state) => ({
  ...makeMapStateToProps(state),
  bookingFormControls: state.scheduleManagement.formControls,
  bookingInvalidControls: state.scheduleManagement.invalidControls,
  bookingFormIsOpen: state.scheduleManagement.bookingFormIsOpen,
  edit: state.scheduleManagement.edit,
  bookingIdToEdit: state.scheduleManagement.bookingIdToEdit,
  completeIsOpen: state.scheduleManagement.completeIsOpen,
  deleteIsOpen: state.scheduleManagement.deleteIsOpen,
  toDeleteId: state.scheduleManagement.toDeleteId,
  suppliersList: Object.values(
    Object.values(state.app.objects.suppliers || {}).reduce((acc, s) => {
      const name = s.companyName || state.app.objects.entities?.[s.entityId || s.entity_id]?.name || s.name || s.business_name || 'Sin Nombre';
      if (!acc[name]) {
        acc[name] = { ...s, name };
      }
      return acc;
    }, {})
  ).sort((a, b) => a.name.localeCompare(b.name)),
  varietiesList: Object.values(state.scheduleManagement.varieties || {}),
  plantsList: Object.values(state.scheduleManagement.plants || {}),
  carriersList: Object.values(state.scheduleManagement.carriers || {}),
  operationTypes: Object.values(state.scheduleManagement.operationTypes || {}),
  truckTypes: Object.values(state.scheduleManagement.truckTypes || {}),
  loadConditions: Object.values(state.scheduleManagement.loadConditions || {}),
  varietyFormIsOpen: state.varieties.formIsOpen,
  varietyFormControls: state.varieties.formControls,
  varietyInvalidControls: state.varieties.invalidControls,
  varietyEdit: state.varieties.edit,
});

const mapDispatchToProps = (dispatch) => {
    const actions = actionProps(clone(clone.OBJECT, suppliersActions, scheduleManagementActions))(dispatch);
    const varietyActions = actionProps(varietiesActions)(dispatch);
    return {
        ...actions,
        varietyActions: varietyActions.actions,
    };
};

const withSaga = connectSaga({ key: 'sagaScheduleManagement', saga });
const withSuppliersSaga = connectSaga({ key: 'sagaSuppliers', saga: suppliersSaga });
const withVarietiesSaga = connectSaga({ key: 'sagaVarieties', saga: varietiesSaga });

export default compose(
  withSaga,
  withSuppliersSaga,
  withVarietiesSaga,
  connect(mapStateToProps, mapDispatchToProps)
)(ScheduleManagement);
